import { useState, useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { Link, useLocation } from "react-router-dom";
// framer-motion was the only above-the-fold consumer of the `motion`
// chunk along with Hero + ThemeToggle. All three have been converted
// to plain CSS (see index.css `navbar-slide-down`, `fade-in`,
// `headline-word-in`, `theme-icon-swap`) so vite no longer
// modulepreloads framer-motion on the lander entry. Saves ~14 KB gzip
// on first paint.
// Inline SVG for the three icons we need — keeps lucide-react out of
// the critical-path bundle. The Navbar is the only file on the
// lander entry that imports from lucide; with these inlined the
// `icons` chunk is no longer reachable from the entry tree, so vite
// stops preloading it on first paint. Each icon is ~150 bytes here vs
// ~6 KB gzip for the icons chunk.
const IconMenu = ({ size = 20, className }: { size?: number; className?: string }) => (
  <svg
    aria-hidden
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    className={className}
  >
    {[6, 12, 18].flatMap((cy) =>
      [6, 12, 18].map((cx) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.8" />
      ))
    )}
  </svg>
);
const IconX = ({ size = 20, className }: { size?: number; className?: string }) => (
  <svg
    aria-hidden
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);
const IconChevronDown = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);
import ThemePicker from "@/components/ThemePicker";
import ScrambleText from "@/components/ScrambleText";
import LetterBold from "@/components/LetterBold";
import lionLogoDark from "@/assets/lion-black.webp";
import lionLogoLight from "@/assets/lion-logo-light.png";

type NavLink =
  | { label: string; href: string }
  | { label: string; to: string }
  | {
      label: string;
      dropdown: Array<{
        label: string;
        to?: string;
        href?: string;
        description?: string;
      }>;
    };

const navLinks: NavLink[] = [
  {
    label: "Product",
    dropdown: [
      { label: "Overview", to: "/product", description: "Everything Mane is and does" },
      { label: "Agents", to: "/agents", description: "All agents, by tier" },
      { label: "AI Agent MCPs", to: "/mcp", description: "Business MCPs for your agent" },
      { label: "Architecture", to: "/architecture", description: "How the system fits together" },
      { label: "Pricing", to: "/pricing", description: "Plans and credit-based usage" },
    ],
  },
  {
    label: "Compare",
    dropdown: [
      { label: "vs ChatGPT (Codex)", to: "/compare/chatgpt", description: "One generalist vs. a team of specialist agents" },
      { label: "vs Claude (in Slack)", to: "/compare/claude", description: "From one bot to a whole team" },
      { label: "vs OpenClaw", to: "/compare/openclaw", description: "Managed Business MCPs, no setup" },
      { label: "vs Hermes", to: "/compare/hermes", description: "Ready to ship, on your Claude" },
    ],
  },
  {
    label: "Resources",
    dropdown: [
      { label: "Example Library", to: "/examples", description: "Real outputs you can download" },
      { label: "Integrations", to: "/integrations", description: "3,000+ tools, SOC 2 Type 2" },
      { label: "Learn", to: "/learn", description: "AI agents, end to end" },
      { label: "Docs", to: "/docs", description: "Get started and reference" },
      
      { label: "Blog", to: "/blog", description: "Field notes from the team" },
      { label: "Changelog", to: "/changelog", description: "What's new in Mane" },
    ],
  },
  { label: "Download", to: "/get-mane" },
];

const Navbar = ({ variant = "default" }: { variant?: "default" | "overlay" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // (hovered link state removed: collapsed nav uses a single "Menu" trigger.)
  const [logoHover, setLogoHover] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownTimer = useRef<number | null>(null);
  const location = useLocation();
  const isLandingOverlay = variant === "overlay" && (location.pathname === "/" || location.pathname === "/product");
  const scrolledEffective = scrolled || mobileOpen;
  const isOverlay = isLandingOverlay && !scrolledEffective;
  // White text/logo only applies on /product overlay (dark video bg).
  // Landing "/" keeps its original black-on-light treatment.
  const isProductOverlay = isOverlay && location.pathname === "/product";

  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted ? resolvedTheme === "dark" : false;
  const darkOpacity = isProductOverlay ? 0 : isDark ? 0 : 1;
  const lightOpacity = isProductOverlay ? 1 : isDark ? 1 : 0;

  useEffect(() => {
    let frame = 0;
    let current = window.scrollY > 20;
    setScrolled(current);
    const handler = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const next = window.scrollY > 20;
        if (next !== current) {
          current = next;
          setScrolled(next);
        }
      });
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      window.removeEventListener("scroll", handler);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const enterDropdown = (label: string) => {
    if (dropdownTimer.current) window.clearTimeout(dropdownTimer.current);
    setOpenDropdown(label);
  };

  const leaveDropdown = () => {
    if (dropdownTimer.current) window.clearTimeout(dropdownTimer.current);
    dropdownTimer.current = window.setTimeout(() => setOpenDropdown(null), 260);
  };

  return (
    <>
      <nav
        className={`animate-navbar-down ${isLandingOverlay ? "fixed" : "sticky"} top-0 left-0 right-0 z-[60] transition-[padding,border-color,background-color] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] border-b ${
          scrolledEffective
            ? "pt-2 sm:pt-4 border-transparent"
            : isOverlay
            ? "pt-2 sm:pt-4 border-transparent"
            : "border-foreground/15"
        }`}
      >
        <div
          style={{ transform: "translateZ(0)" }}
          className={`mx-auto isolate transition-[max-width,border-radius,background-color,box-shadow,backdrop-filter] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[max-width,box-shadow] ${
            scrolledEffective
              ? "max-w-[min(1180px,calc(100%-0.5rem))] sm:max-w-[min(1180px,calc(100%-1.5rem))] rounded-full border border-border bg-background/75 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 shadow-[0_6px_18px_-14px_rgba(15,12,8,0.12),0_1px_3px_-2px_rgba(15,12,8,0.05)] dark:shadow-[0_8px_22px_-16px_rgba(0,0,0,0.45),0_1px_3px_-2px_rgba(0,0,0,0.3)]"
              : "max-w-none w-full"
          }`}
        >
          <div className="hidden lg:flex relative items-center justify-between h-14 px-6 xl:px-10 2xl:px-14">
            {/* Left: wordmark */}
            <Link
              to="/"
              onMouseEnter={() => setLogoHover(true)}
              onMouseLeave={() => setLogoHover(false)}
              onFocus={() => setLogoHover(true)}
              onBlur={() => setLogoHover(false)}
              className="flex items-center gap-2.5 group -mx-2 px-2 py-2 relative z-10"
            >
              {/* Crossfade pure white↔black mark on the same 600ms curve as the pill bg. Light logo shows initially in dark mode. */}
              <span className="relative inline-block w-6 h-6">
                <img decoding="async" src={lionLogoDark} alt="" aria-hidden="true" width={24} height={24} fetchPriority="high" data-no-fade style={{ opacity: darkOpacity }} className="absolute inset-0 w-6 h-6 object-contain transition-opacity duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                <img decoding="async" src={lionLogoLight} alt="" aria-hidden="true" width={24} height={24} fetchPriority="high" data-no-fade style={{ opacity: lightOpacity }} className="absolute inset-0 w-6 h-6 object-contain transition-opacity duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
              </span>
              <span
                className="inline-block w-[72px] overflow-hidden"
                style={{ fontWeight: 900, letterSpacing: "-0.055em" }}
              >
                <ScrambleText
                  text="mane"
                  trigger={logoHover}
                  duration={420}
                  className={`font-sans text-[22px] leading-none inline-block whitespace-nowrap ${isProductOverlay ? "text-white" : "text-foreground"}`}
                />
              </span>
            </Link>

            {/* Middle: single "Menu" trigger. Expands a mega-panel on
                hover containing every section (Product, Pricing,
                Compare, Resources) so the bar stays minimal. */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-3"
              onMouseEnter={() => enterDropdown("__menu__")}
              onMouseLeave={leaveDropdown}
            >
              <button
                type="button"
                onClick={() =>
                  setOpenDropdown((c) => (c === "__menu__" ? null : "__menu__"))
                }
                className="px-[10px] py-0 leading-none"
                aria-haspopup="menu"
                aria-expanded={openDropdown === "__menu__"}
              >
                <span
                  className={`inline-flex items-center gap-1 font-sans text-[14px] font-normal leading-none transition-colors ${
                    isProductOverlay ? "text-white" : "text-foreground"
                  }`}
                >
                  <LetterBold text="Menu" />
                  <IconMenu
                    size={16}
                    className={`transition-transform ${
                      ""
                    }`}
                  />

                </span>
              </button>

              {openDropdown === "__menu__" && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 w-[min(720px,calc(100vw-2rem))] transition-[opacity,transform] duration-150 ease-out opacity-100 translate-y-0"
                  role="menu"
                >
                  <div className="rounded-[6px] border border-border bg-background overflow-hidden shadow-[0_24px_48px_-12px_rgba(15,12,8,0.28),0_8px_16px_-8px_rgba(15,12,8,0.18),0_0_0_1px_rgba(15,12,8,0.04)] dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.7),0_8px_16px_-8px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.04)]">
                    <div className="grid grid-cols-3 gap-px bg-border/60">
                      {navLinks
                        .filter((l) => l.label !== "Get Started")
                        .map((link) => (
                          <div key={link.label} className="bg-background p-5">
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                              {link.label}
                            </p>
                            <div className="flex flex-col gap-2.5">
                              {"dropdown" in link ? (
                                link.dropdown.map((item) => {
                                  const inner = (
                                    <>
                                      <LetterBold text={item.label} className="font-sans text-[13.5px] text-foreground leading-snug" />
                                      {item.description && (
                                        <p className="text-[11.5px] text-muted-foreground/70 leading-snug mt-0.5">
                                          {item.description}
                                        </p>
                                      )}
                                    </>
                                  );
                                  const key = item.to ?? item.href ?? item.label;
                                  if (item.href) {
                                    return (
                                      <a
                                        key={key}
                                        href={item.href}
                                        onClick={() => setOpenDropdown(null)}
                                        className="group block"
                                        role="menuitem"
                                      >
                                        {inner}
                                      </a>
                                    );
                                  }
                                  return (
                                    <Link
                                      key={key}
                                      to={item.to ?? "/"}
                                      onClick={() => setOpenDropdown(null)}
                                      className="group block"
                                      role="menuitem"
                                    >
                                      {inner}
                                    </Link>
                                  );
                                })
                              ) : "to" in link ? (
                                <Link
                                  to={link.to}
                                  onClick={() => setOpenDropdown(null)}
                                  className="group block"
                                  role="menuitem"
                                >
                                  <LetterBold text={link.label} className="font-sans text-[13.5px] text-foreground leading-snug" />
                                </Link>
                              ) : null}
                            </div>
                          </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-border bg-background">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Theme</span>
                      <ThemePicker />
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* Right: Sign in, Get Started */}
            <div className="flex items-center gap-3">
              {/* Sign in, text link */}
              <a
                href="https://auth.mane.dev"
                className={`inline-flex items-center gap-1 font-sans text-[14px] font-normal leading-none ${
                  isProductOverlay ? "text-white" : "text-foreground"
                }`}
              >
                <LetterBold text="Sign in" />
              </a>

              <a
                href="https://auth.mane.dev"
                className={`inline-flex items-center px-4 h-9 rounded-full font-sans text-[14px] font-normal leading-none shadow-[0_1px_2px_rgba(0,0,0,0.18),0_8px_24px_-12px_rgba(0,0,0,0.35)] hover:-translate-y-px active:scale-[0.97] transition-[transform,background-color,opacity,box-shadow] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isOverlay
                    ? "bg-white text-[#0a0a0a] hover:bg-white/95"
                    : "bg-foreground text-background hover:opacity-90"
                }`}
              >
                Get Started
              </a>
            </div>

          </div>

          {/* Mobile bar */}
          <div className="lg:hidden flex items-center justify-between h-14 px-3.5 sm:px-5">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="relative inline-block w-8 h-8 sm:w-9 sm:h-9">
                <img decoding="async" src={lionLogoDark} alt="" aria-hidden="true" width={36} height={36} fetchPriority="high" data-no-fade style={{ opacity: darkOpacity }} className="absolute inset-0 w-8 h-8 sm:w-9 sm:h-9 object-contain transition-opacity duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                <img decoding="async" src={lionLogoLight} alt="" aria-hidden="true" width={36} height={36} fetchPriority="high" data-no-fade style={{ opacity: lightOpacity }} className="absolute inset-0 w-8 h-8 sm:w-9 sm:h-9 object-contain transition-opacity duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
              </span>
              <span
                className={`font-sans text-[24px] sm:text-[26px] leading-none ${isProductOverlay ? "text-white" : "text-foreground"}`}
                style={{ fontWeight: 900, letterSpacing: "-0.055em" }}
              >
                mane
              </span>
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center ${isProductOverlay ? "text-white" : "text-foreground"}`}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <IconX size={26} /> : <IconMenu size={26} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay — same conditional render, just a CSS
          opacity transition instead of AnimatePresence. The fade on
          open is preserved; close is instant (unmount). Acceptable
          trade-off to keep framer-motion off the critical path. */}
      {mobileOpen && (
          <div
            className="fixed inset-0 z-40 pt-12 animate-[fade-in_0.15s_ease-out_both]"
          >
            <div className="absolute inset-0 bg-background" />
            <div className="relative h-full overflow-y-auto p-5">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => {
                  if ("dropdown" in link) {
                    const expanded = mobileExpanded === link.label;
                    return (
                      <div
                        key={link.label}
                        className="border-b border-border/60"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setMobileExpanded((c) => (c === link.label ? null : link.label))
                          }
                          className="w-full px-4 py-3 font-mono text-[13px] uppercase tracking-[0.2em] text-foreground hover:bg-secondary rounded-none transition-colors min-h-[44px] flex items-center justify-between"
                        >
                          <span>{link.label}</span>
                          <IconChevronDown
                            className={`w-4 h-4 transition-transform ${
                              expanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {expanded && (
                          <div className="pb-3 pl-4 flex flex-col gap-1">
                            {link.dropdown.map((item) => {
                              const cls =
                                "px-4 py-2.5 font-mono text-[12px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground hover:bg-secondary rounded-none transition-colors min-h-[40px] flex items-center";
                              const key = item.to ?? item.href ?? item.label;
                              if (item.href) {
                                return (
                                  <a
                                    key={key}
                                    href={item.href}
                                    onClick={() => setMobileOpen(false)}
                                    className={cls}
                                  >
                                    {item.label}
                                  </a>
                                );
                              }
                              return (
                                <Link
                                  key={key}
                                  to={item.to ?? "/"}
                                  onClick={() => setMobileOpen(false)}
                                  className={cls}
                                >
                                  {item.label}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return "to" in link ? (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 font-mono text-[13px] uppercase tracking-[0.2em] text-foreground hover:bg-secondary rounded-none transition-colors min-h-[44px] flex items-center border-b border-border/60"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 font-mono text-[13px] uppercase tracking-[0.2em] text-foreground hover:bg-secondary rounded-none transition-colors min-h-[44px] flex items-center border-b border-border/60"
                    >
                      {link.label}
                    </a>
                  );
                })}
                <a
                  href="https://auth.mane.dev"
                  onClick={() => setMobileOpen(false)}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full h-12 font-sans text-[14px] font-normal bg-foreground text-background shadow-[0_1px_2px_rgba(0,0,0,0.18)]"
                >
                  Get Started <span aria-hidden>→</span>
                </a>
                <div className="mt-6 flex items-center justify-between px-1">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Theme</span>
                  <ThemePicker />
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default Navbar;
